// A small, dependency-free XML parser and writer used by MusicXML import and
// export. It works the same in Node and browsers (no DOM). The parser is
// tolerant: it skips processing instructions, comments and DOCTYPE
// declarations (including internal subsets), decodes predefined and numeric
// entities, accepts unquoted attribute values and recovers from mismatched
// closing tags.

export interface XmlElement {
  name: string;
  attrs: Record<string, string>;
  children: XmlNode[];
}

export type XmlNode = XmlElement | string;

const NAMED_ENTITIES: Record<string, string> = {
  lt: '<',
  gt: '>',
  amp: '&',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  copy: '©',
  reg: '®',
  hellip: '…',
  ndash: '–',
  mdash: '—',
  lsquo: '‘',
  rsquo: '’',
  ldquo: '“',
  rdquo: '”',
  flat: '♭',
  sharp: '♯',
  natural: '♮',
};

/** Decode predefined, common named and numeric character references. Unknown entities are kept verbatim. */
export function decodeEntities(s: string): string {
  if (s.indexOf('&') < 0) return s;
  return s.replace(/&(#x[0-9a-fA-F]+|#[0-9]+|[A-Za-z][A-Za-z0-9]*);/g, (m, body: string) => {
    if (body[0] === '#') {
      const code = body[1] === 'x' || body[1] === 'X' ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
      if (!Number.isFinite(code) || code < 0 || code > 0x10ffff) return m;
      try {
        return String.fromCodePoint(code);
      } catch {
        return m;
      }
    }
    const v = NAMED_ENTITIES[body];
    return v ?? m;
  });
}

function isNameChar(c: number): boolean {
  // Anything that is not whitespace, '/', '>', '=', quotes or '<'.
  return !(c <= 32 || c === 47 || c === 62 || c === 61 || c === 34 || c === 39 || c === 60);
}

/**
 * Parse an XML document and return its root element. Throws when the text
 * contains no element at all.
 */
export function parseXml(text: string): XmlElement {
  const src = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const n = src.length;
  const docRoot: XmlElement = { name: '#document', attrs: {}, children: [] };
  const stack: XmlElement[] = [docRoot];
  let i = 0;
  const top = () => stack[stack.length - 1];
  const pushText = (t: string) => {
    if (!t) return;
    const kids = top().children;
    const last = kids[kids.length - 1];
    if (typeof last === 'string') kids[kids.length - 1] = last + t;
    else kids.push(t);
  };

  while (i < n) {
    const lt = src.indexOf('<', i);
    if (lt < 0) {
      if (stack.length > 1) pushText(decodeEntities(src.slice(i)));
      break;
    }
    if (lt > i && stack.length > 1) pushText(decodeEntities(src.slice(i, lt)));
    i = lt;
    if (src.startsWith('<!--', i)) {
      const end = src.indexOf('-->', i + 4);
      i = end < 0 ? n : end + 3;
      continue;
    }
    if (src.startsWith('<![CDATA[', i)) {
      const end = src.indexOf(']]>', i + 9);
      const body = src.slice(i + 9, end < 0 ? n : end);
      if (stack.length > 1) pushText(body);
      i = end < 0 ? n : end + 3;
      continue;
    }
    if (src.startsWith('<?', i)) {
      const end = src.indexOf('?>', i + 2);
      i = end < 0 ? n : end + 2;
      continue;
    }
    if (src.startsWith('<!', i)) {
      // DOCTYPE or other declaration; skip quoted strings and an internal [subset].
      let j = i + 2;
      let depth = 0;
      while (j < n) {
        const c = src[j];
        if (c === '"' || c === "'") {
          const e = src.indexOf(c, j + 1);
          j = e < 0 ? n : e + 1;
          continue;
        }
        if (c === '[') depth++;
        else if (c === ']') depth = Math.max(0, depth - 1);
        else if (c === '>' && depth === 0) break;
        else if (c === '<' && src.startsWith('<!--', j)) {
          const e = src.indexOf('-->', j + 4);
          j = e < 0 ? n : e + 3;
          continue;
        }
        j++;
      }
      i = j + 1;
      continue;
    }
    if (src[i + 1] === '/') {
      const gt = src.indexOf('>', i + 2);
      const name = src.slice(i + 2, gt < 0 ? n : gt).trim();
      i = gt < 0 ? n : gt + 1;
      // Pop to the matching element; ignore stray closing tags.
      for (let k = stack.length - 1; k >= 1; k--) {
        if (stack[k].name === name) {
          stack.length = k;
          break;
        }
      }
      continue;
    }
    // Start tag.
    let j = i + 1;
    while (j < n && isNameChar(src.charCodeAt(j))) j++;
    const name = src.slice(i + 1, j);
    if (!name) {
      // A lone '<' in text: keep it as text.
      if (stack.length > 1) pushText('<');
      i++;
      continue;
    }
    const el: XmlElement = { name, attrs: {}, children: [] };
    let selfClosing = false;
    // Attributes.
    for (;;) {
      while (j < n && src.charCodeAt(j) <= 32) j++;
      if (j >= n) break;
      const c = src[j];
      if (c === '>') {
        j++;
        break;
      }
      if (c === '/' && src[j + 1] === '>') {
        selfClosing = true;
        j += 2;
        break;
      }
      if (c === '<') break; // unterminated start tag: let the next token begin here
      if (c === '/') {
        j++;
        continue;
      }
      const ns = j;
      while (j < n && isNameChar(src.charCodeAt(j))) j++;
      const an = src.slice(ns, j);
      if (!an) {
        j++;
        continue;
      }
      while (j < n && src.charCodeAt(j) <= 32) j++;
      if (src[j] !== '=') {
        el.attrs[an] = '';
        continue;
      }
      j++;
      while (j < n && src.charCodeAt(j) <= 32) j++;
      const q = src[j];
      let value: string;
      if (q === '"' || q === "'") {
        const e = src.indexOf(q, j + 1);
        value = src.slice(j + 1, e < 0 ? n : e);
        j = e < 0 ? n : e + 1;
      } else {
        const vs = j;
        while (j < n && src.charCodeAt(j) > 32 && src[j] !== '>' && !(src[j] === '/' && src[j + 1] === '>')) j++;
        value = src.slice(vs, j);
      }
      el.attrs[an] = decodeEntities(value);
    }
    i = j;
    top().children.push(el);
    if (!selfClosing) stack.push(el);
  }
  const root = docRoot.children.find((c): c is XmlElement => typeof c !== 'string');
  if (!root) throw new Error('Not an XML document (no root element)');
  return root;
}

// ---------------------------------------------------------------- queries

/** Local name without a namespace prefix. */
export function localName(el: XmlElement): string {
  const k = el.name.indexOf(':');
  return k < 0 ? el.name : el.name.slice(k + 1);
}

export function elements(el: XmlElement | undefined, name?: string): XmlElement[] {
  if (!el) return [];
  const out: XmlElement[] = [];
  for (const c of el.children) if (typeof c !== 'string' && (name === undefined || c.name === name)) out.push(c);
  return out;
}

export function child(el: XmlElement | undefined, name: string): XmlElement | undefined {
  if (!el) return undefined;
  for (const c of el.children) if (typeof c !== 'string' && c.name === name) return c;
  return undefined;
}

/** First descendant along a path of element names. */
export function path(el: XmlElement | undefined, ...names: string[]): XmlElement | undefined {
  let cur = el;
  for (const nm of names) {
    cur = child(cur, nm);
    if (!cur) return undefined;
  }
  return cur;
}

/** Concatenated text of an element and its descendants. */
export function textOf(el: XmlElement | undefined): string {
  if (!el) return '';
  let s = '';
  for (const c of el.children) s += typeof c === 'string' ? c : textOf(c);
  return s;
}

/** Trimmed text of a child element, or undefined when absent. */
export function childText(el: XmlElement | undefined, name: string): string | undefined {
  const c = child(el, name);
  return c ? textOf(c).trim() : undefined;
}

/** Numeric value of a child element (NaN-safe: undefined when absent or not a number). */
export function childNum(el: XmlElement | undefined, name: string): number | undefined {
  const t = childText(el, name);
  if (t === undefined || t === '') return undefined;
  const v = Number(t);
  return Number.isFinite(v) ? v : undefined;
}

export function attrNum(el: XmlElement | undefined, name: string): number | undefined {
  const t = el?.attrs[name];
  if (t === undefined || t === '') return undefined;
  const v = Number(t);
  return Number.isFinite(v) ? v : undefined;
}

// ---------------------------------------------------------------- writing

export function escapeXml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&apos;'));
}

/** Strip characters that are not allowed in XML 1.0. */
function cleanXmlChars(s: string): string {
  // eslint-disable-next-line no-control-regex
  return s.replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f￾￿]/g, '');
}

export type XmlAttrs = Record<string, string | number | boolean | undefined | null>;

function attrString(attrs?: XmlAttrs): string {
  if (!attrs) return '';
  let s = '';
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === null || v === false) continue;
    s += ` ${k}="${escapeXml(cleanXmlChars(v === true ? 'yes' : String(v)))}"`;
  }
  return s;
}

/** Indented XML writer. */
export class XmlWriter {
  private out: string[] = [];
  private depth = 0;
  constructor(private indent = '  ') {}

  raw(line: string): this {
    this.out.push(this.indent.repeat(this.depth) + line);
    return this;
  }

  open(name: string, attrs?: XmlAttrs): this {
    this.raw(`<${name}${attrString(attrs)}>`);
    this.depth++;
    return this;
  }

  close(name: string): this {
    this.depth--;
    this.raw(`</${name}>`);
    return this;
  }

  /** `<name attrs>text</name>`, or `<name attrs/>` when text is undefined/empty. */
  leaf(name: string, text?: string | number, attrs?: XmlAttrs): this {
    if (text === undefined || text === '') return this.raw(`<${name}${attrString(attrs)}/>`);
    return this.raw(`<${name}${attrString(attrs)}>${escapeXml(cleanXmlChars(String(text)))}</${name}>`);
  }

  toString(): string {
    return this.out.join('\n') + '\n';
  }
}

// ---------------------------------------------------------------- decoding

/** Decode XML bytes honouring a BOM or the encoding in the XML declaration (UTF-8 default). */
export function decodeXmlBytes(bytes: Uint8Array): string {
  if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xfe) return new TextDecoder('utf-16le').decode(bytes.subarray(2));
  if (bytes.length >= 2 && bytes[0] === 0xfe && bytes[1] === 0xff) return new TextDecoder('utf-16be').decode(bytes.subarray(2));
  if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) return new TextDecoder('utf-8').decode(bytes.subarray(3));
  // UTF-16 without BOM: '<' followed by 0.
  if (bytes.length >= 4 && bytes[0] === 0x3c && bytes[1] === 0 && bytes[2] !== 0) return new TextDecoder('utf-16le').decode(bytes);
  if (bytes.length >= 4 && bytes[0] === 0 && bytes[1] === 0x3c) return new TextDecoder('utf-16be').decode(bytes);
  const head = new TextDecoder('latin1').decode(bytes.subarray(0, Math.min(200, bytes.length)));
  const m = /^<\?xml[^>]*encoding\s*=\s*["']([A-Za-z0-9._-]+)["']/.exec(head);
  const enc = m ? m[1].toLowerCase() : 'utf-8';
  if (enc !== 'utf-8' && enc !== 'utf8') {
    try {
      return new TextDecoder(enc).decode(bytes);
    } catch {
      // Unknown label: fall through to UTF-8.
    }
  }
  return new TextDecoder('utf-8').decode(bytes);
}
